import { useMediaQuery, useTheme } from '@mui/material';
import type { GridColumnVisibilityModel, GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { useOrderByParams } from '~/hooks/order-by';
import TVBrandIllustration from '~/images/illustrations/tv-brands.svg';
import type { NetworkTvBrands__TvBrand as TVBrand } from '../../queries/list.generated';
import { useColumns } from '../lib/columns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={TVBrandIllustration}
      description={t('pages.tv_brands.list.empty_state.description')}
      header={t('pages.tv_brands.list.empty_state.header')}
    />
  );
};

export interface TableProps {
  tvBrands: readonly TVBrand[];
  loading: boolean;
}

export const Table = ({ tvBrands, loading }: TableProps) => {
  const columns = useColumns();
  const [tvBrandColumns, setColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  const { setOrderByParams, sortModel } = useOrderByParams();

  const onSortModelChange = useCallback(
    (sortModel: GridSortModel) => {
      setOrderByParams(sortModel.at(0) ?? null);
    },
    [setOrderByParams],
  );

  useEffect(() => {
    setColumns((x) => ({
      ...x,
      createdAt: !isSmallAndDown,
      devices: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={tvBrandColumns}
      disableRowSelectionOnClick
      loading={loading}
      onColumnVisibilityModelChange={setColumns}
      onRowClick={({ row }: GridRowParams<TVBrand>) => navigate(String(row.id))}
      rows={tvBrands}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
    />
  );
};
